import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, Image as ImageIcon, Inbox, Gauge, HelpCircle, Loader2 } from "lucide-react";
import { PostsManager } from "@/components/admin/PostsManager";
import { ImagesManager } from "@/components/admin/ImagesManager";
import { InquiriesManager } from "@/components/admin/InquiriesManager";
import { CounterSettings } from "@/components/admin/CounterSettings";
import { FaqsManager } from "@/components/admin/FaqsManager";
import { SEO } from "@/components/nyla/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getAdminToken = () => localStorage.getItem("nyla_admin_token");
export const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("posts");
  const [otpStep, setOtpStep] = useState(null); // {otp_id, email_hint}
  const [code, setCode] = useState("");

  useEffect(() => {
    const token = getAdminToken();
    if (!token) return setAuthed(false);
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem("nyla_admin_token");
        setAuthed(false);
      });
  }, []);

  const login = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password });
      if (data.otp_required) {
        setOtpStep({ otp_id: data.otp_id, email_hint: data.email_hint });
        setCode("");
        if (data.email_sent === false) {
          toast.error(data.detail || "Verification email could not be sent.");
        } else {
          toast.success(`Verification code sent to ${data.email_hint}`);
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { otp_id: otpStep.otp_id, code: code.trim() });
      localStorage.setItem("nyla_admin_token", data.token);
      setAuthed(true);
      setOtpStep(null);
      setPassword("");
      toast.success("Welcome back");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Verification failed");
      if (typeof d === "string" && d.includes("log in again")) {
        setOtpStep(null);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("nyla_admin_token");
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <SEO title="Admin" path="/admin" noindex />
        <Loader2 className="w-6 h-6 animate-spin text-[#86868B]" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-6 font-body">
        <SEO title="Admin Sign-in" path="/admin" noindex />
        {otpStep ? (
          <form onSubmit={verifyOtp} data-testid="admin-otp-form" className="w-full max-w-sm bg-white rounded-3xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
            <div className="flex items-center gap-3 mb-8">
              <img src="/nyla-logo.png" alt="Nyla" className="h-12 w-auto" />
              <span className="font-display text-lg font-bold tracking-tight">Two-Factor Verification</span>
            </div>
            <p className="text-sm text-[#86868B] mb-6">
              We emailed a 6-digit code to <span className="font-semibold text-[#1D1D1F]">{otpStep.email_hint}</span>. It expires in 5 minutes.
            </p>
            <input
              data-testid="admin-otp-input"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-5 py-3.5 text-center text-xl tracking-[0.5em] mb-4 focus:outline-none focus:border-[#2C3E35]"
            />
            <button
              data-testid="admin-otp-verify-button"
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-[0.98] transition-transform duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Verify & Sign In
            </button>
            <div className="flex justify-between mt-5">
              <button type="button" data-testid="admin-otp-back" onClick={() => { setOtpStep(null); setCode(""); }} className="text-xs text-[#86868B] hover:text-[#1D1D1F]">
                ← Back
              </button>
              <button type="button" data-testid="admin-otp-resend" onClick={login} disabled={loading} className="text-xs font-medium text-[#2C3E35] hover:underline disabled:opacity-50">
                Resend code
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
            <div className="flex items-center gap-3 mb-8">
              <img src="/nyla-logo.png" alt="Nyla" className="h-12 w-auto" />
              <span className="font-display text-lg font-bold tracking-tight">Admin</span>
            </div>
            <p className="text-sm text-[#86868B] mb-6">Enter the team password. We'll email you a verification code.</p>
            <input
              data-testid="admin-password-input"
              type="password"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-5 py-3.5 text-sm mb-4 focus:outline-none focus:border-[#2C3E35]"
            />
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#1D1D1F] text-white text-sm font-medium hover:bg-black active:scale-[0.98] transition-transform duration-200 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Continue
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F5F7] font-body text-[#1D1D1F]">
      <SEO title="Admin Dashboard" path="/admin" noindex />
      <header className="sticky top-0 z-40 bg-[#F5F5F7]/80 backdrop-blur-2xl border-b border-[#D2D2D7]/60">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src="/nyla-logo.png" alt="Nyla" className="h-9 w-auto" />
            <span className="font-display text-base font-bold tracking-tight">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-tab-posts"
              onClick={() => setTab("posts")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${tab === "posts" ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
            >
              <FileText className="w-3.5 h-3.5" /> Blog Posts
            </button>
            <button
              data-testid="admin-tab-images"
              onClick={() => setTab("images")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${tab === "images" ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
            >
              <ImageIcon className="w-3.5 h-3.5" /> Site Images
            </button>
            <button
              data-testid="admin-tab-inquiries"
              onClick={() => setTab("inquiries")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${tab === "inquiries" ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
            >
              <Inbox className="w-3.5 h-3.5" /> Inquiries
            </button>
            <button
              data-testid="admin-tab-faqs"
              onClick={() => setTab("faqs")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${tab === "faqs" ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
            >
              <HelpCircle className="w-3.5 h-3.5" /> FAQs
            </button>
            <button
              data-testid="admin-tab-counter"
              onClick={() => setTab("counter")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${tab === "counter" ? "bg-[#1D1D1F] text-white" : "text-[#1D1D1F]/70 hover:bg-black/5"}`}
            >
              <Gauge className="w-3.5 h-3.5" /> Counter
            </button>
            <button data-testid="admin-logout" onClick={logout} className="ml-2 p-2 rounded-full hover:bg-black/5" aria-label="Log out">
              <LogOut className="w-4 h-4 text-[#86868B]" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">
        {tab === "posts" && <PostsManager onUnauthorized={logout} />}
        {tab === "images" && <ImagesManager onUnauthorized={logout} />}
        {tab === "inquiries" && <InquiriesManager onUnauthorized={logout} />}
        {tab === "faqs" && <FaqsManager onUnauthorized={logout} />}
        {tab === "counter" && <CounterSettings onUnauthorized={logout} />}
      </main>
    </div>
  );
}
